import i18n from '../i18n'

export interface LocalizationConfig {
  language: string
  currency: string
  currencySymbol: string
  price: number // Prix en centimes
  priceFormatted: string
  country: string
  locale: string
}

export interface CountryConfig {
  currency: string
  currencySymbol: string
  price: number // Prix en centimes (toujours 4999 = 49.99)
  locale: string
}

// Configuration des pays supportés
const COUNTRY_CONFIGS: Record<string, CountryConfig> = {
  // France et pays européens utilisant l'Euro
  FR: { currency: 'EUR', currencySymbol: '€', price: 4999, locale: 'fr-FR' },
  BE: { currency: 'EUR', currencySymbol: '€', price: 4999, locale: 'fr-BE' },
  LU: { currency: 'EUR', currencySymbol: '€', price: 4999, locale: 'fr-LU' },
  DE: { currency: 'EUR', currencySymbol: '€', price: 4999, locale: 'de-DE' },
  AT: { currency: 'EUR', currencySymbol: '€', price: 4999, locale: 'de-AT' },
  ES: { currency: 'EUR', currencySymbol: '€', price: 4999, locale: 'es-ES' },
  IT: { currency: 'EUR', currencySymbol: '€', price: 4999, locale: 'it-IT' },

  // Suisse utilisant le Franc suisse
  CH: { currency: 'CHF', currencySymbol: 'CHF', price: 4999, locale: 'de-CH' },

  // Royaume-Uni utilisant la Livre sterling
  GB: { currency: 'GBP', currencySymbol: '£', price: 4999, locale: 'en-GB' },
  
  // États-Unis utilisant le Dollar américain
  US: { currency: 'USD', currencySymbol: '$', price: 4999, locale: 'en-US' },
  
  // Canada utilisant le Dollar canadien
  CA: { currency: 'CAD', currencySymbol: 'C$', price: 6999, locale: 'en-CA' },
  
  // Australie utilisant le Dollar australien
  AU: { currency: 'AUD', currencySymbol: 'A$', price: 7999, locale: 'en-AU' },
  
  // Nouvelle-Zélande utilisant le Dollar néo-zélandais
  NZ: { currency: 'NZD', currencySymbol: 'NZ$', price: 7999, locale: 'en-NZ' },

  // Mexique (peso mexicain) — heure ≈ équiv. 6,90 €/h ; abos = vraie conversion FX
  MX: { currency: 'MXN', currencySymbol: 'MX$', price: 99900, locale: 'es-MX' },

  // Argentine (peso argentin) — ⚠️ à RÉAJUSTER régulièrement (inflation/taux)
  AR: { currency: 'ARS', currencySymbol: 'AR$', price: 7090000, locale: 'es-AR' },

  // Défaut (EUR — exigence Hipay : prix affichés en euros par défaut)
  DEFAULT: { currency: 'EUR', currencySymbol: '€', price: 4999, locale: 'en-US' }
}

const SUPPORTED_LANGS = ['en', 'fr', 'de', 'es', 'it']

// Mapping pays -> langue UI (utilisé en fallback si la langue navigateur n'est pas supportée)
const COUNTRY_TO_LANG: Record<string, string> = {
  FR: 'fr', BE: 'fr', LU: 'fr',
  DE: 'de', AT: 'de', CH: 'de',
  ES: 'es', MX: 'es', AR: 'es',
  IT: 'it'
}

// Mapping langue -> pays par défaut
const LANGUAGE_TO_COUNTRY: Record<string, string> = {
  'fr': 'FR',
  'fr-FR': 'FR',
  'fr-BE': 'BE',
  'fr-CA': 'CA',
  'en': 'US',
  'en-US': 'US',
  'en-GB': 'GB', // Royaume-Uni utilise GBP
  'en-CA': 'CA',
  'en-AU': 'AU',
  'en-NZ': 'NZ',
  'de': 'DE',
  'es': 'ES',
  'it': 'IT'
}

class LocalizationService {
  private currentConfig: LocalizationConfig | null = null

  private async detectCountryByIpApi(): Promise<string | null> {
    try {
      console.log('🌍 Détection du pays via IP (ipapi.co)...')
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000) // Timeout de 5 secondes

      const response = await fetch('https://ipapi.co/country_code/', {
        signal: controller.signal,
        headers: {
          Accept: 'text/plain',
        },
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        console.warn('🌍 Erreur API ipapi.co:', response.status)
        return null
      }

      const countryCode = await response.text()
      const detectedCountry = countryCode.trim().toUpperCase()
      console.log('🌍 Pays détecté via ipapi.co:', detectedCountry)

      // Vérifier que le pays est supporté
      if (COUNTRY_CONFIGS[detectedCountry]) {
        return detectedCountry
      }

      console.log('🌍 Pays non supporté (ipapi.co):', detectedCountry)
      return null
    } catch (error) {
      console.warn('🌍 Échec détection IP via ipapi.co:', error)
      return null
    }
  }

  private async detectCountryByCloudflare(): Promise<string | null> {
    try {
      console.log('🌍 Détection du pays via IP (Cloudflare trace)...')
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000)

      const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace', {
        signal: controller.signal,
        headers: {
          Accept: 'text/plain',
        },
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        console.warn('🌍 Erreur Cloudflare trace:', response.status)
        return null
      }

      const text = await response.text()
      // Format: key=value\n... including loc=AU
      const locLine = text
        .split('\n')
        .map((l) => l.trim())
        .find((l) => l.startsWith('loc='))

      const loc = locLine?.split('=')[1]?.trim()?.toUpperCase()
      if (!loc) {
        console.warn('🌍 Cloudflare trace: loc non trouvé')
        return null
      }

      console.log('🌍 Pays détecté via Cloudflare:', loc)

      if (COUNTRY_CONFIGS[loc]) {
        return loc
      }

      console.log('🌍 Pays non supporté (Cloudflare):', loc)
      return null
    } catch (error) {
      console.warn('🌍 Échec détection IP via Cloudflare:', error)
      return null
    }
  }

  /**
   * Détecte le pays de l'utilisateur via son adresse IP
   */
  private async detectCountryByIP(): Promise<string> {
    const ipApiCountry = await this.detectCountryByIpApi()
    if (ipApiCountry) return ipApiCountry

    const cfCountry = await this.detectCountryByCloudflare()
    if (cfCountry) return cfCountry

    console.log('🌍 Aucun provider IP disponible, fallback sur langue')
    return this.detectCountryFromLanguage()
  }

  /**
   * Détecte le pays de l'utilisateur basé sur la langue du navigateur (fallback)
   */
  private detectCountryFromLanguage(): string {
    const language = navigator.language || navigator.languages?.[0] || 'en-US'
    console.log('🌍 Langue détectée:', language)
    
    // Chercher une correspondance exacte d'abord
    if (LANGUAGE_TO_COUNTRY[language]) {
      return LANGUAGE_TO_COUNTRY[language]
    }
    
    // Chercher par code de langue principal (ex: 'fr' pour 'fr-CH')
    const mainLanguage = language.split('-')[0]
    if (LANGUAGE_TO_COUNTRY[mainLanguage]) {
      return LANGUAGE_TO_COUNTRY[mainLanguage]
    }
    
    // Défaut
    return 'US'
  }

  /**
   * Détecte la configuration de localisation
   */
  public async detectLocalization(): Promise<LocalizationConfig> {
    const country = await this.detectCountryByIP()
    const countryConfig = COUNTRY_CONFIGS[country] || COUNTRY_CONFIGS.DEFAULT

    // Langue UI: priorité 1) URL ?lng=, 2) préférence navigateur si supportée,
    // 3) mapping pays, 4) anglais. Garde l'alignement avec i18n.language.
    const urlLang = new URLSearchParams(window.location.search).get('lng')
    const browserLang = (navigator.language || navigator.languages?.[0] || '').split('-')[0]
    let language: string
    if (urlLang && SUPPORTED_LANGS.includes(urlLang)) {
      language = urlLang
    } else if (SUPPORTED_LANGS.includes(browserLang)) {
      language = browserLang
    } else if (COUNTRY_TO_LANG[country]) {
      language = COUNTRY_TO_LANG[country]
    } else {
      language = 'en'
    }
    
    // Formater le prix selon la locale
    const priceFormatted = this.formatPrice(countryConfig.price, countryConfig.currency, countryConfig.locale)
    
    const config: LocalizationConfig = {
      language,
      currency: countryConfig.currency,
      currencySymbol: countryConfig.currencySymbol,
      price: countryConfig.price,
      priceFormatted,
      country,
      locale: countryConfig.locale
    }
    
    console.log('🌍 Configuration de localisation détectée:', config)
    this.currentConfig = config
    
    return config
  }

  /**
   * Formate un prix selon la locale
   */
  public formatPrice(priceInCents: number, currency: string, locale: string): string {
    const price = priceInCents / 100
    
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(price)
    } catch (error) {
      console.warn('⚠️ Erreur de formatage du prix, utilisation du fallback:', error)
      // Fallback simple
      if (currency === 'EUR') {
        return `${price.toFixed(2).replace('.', ',')} €`
      } else {
        return `$${price.toFixed(2)}`
      }
    }
  }

  /**
   * Obtient la configuration actuelle
   */
  public async getCurrentConfig(): Promise<LocalizationConfig> {
    if (!this.currentConfig) {
      return await this.detectLocalization()
    }
    return this.currentConfig
  }

  /**
   * Change la langue manuellement
   */
  public async changeLanguage(language: string): Promise<void> {
    await i18n.changeLanguage(language)
    
    // Mettre à jour la configuration
    const country = language === 'fr' ? 'FR' : 'US'
    const countryConfig = COUNTRY_CONFIGS[country]
    
    this.currentConfig = {
      language,
      currency: countryConfig.currency,
      currencySymbol: countryConfig.currencySymbol,
      price: countryConfig.price,
      priceFormatted: this.formatPrice(countryConfig.price, countryConfig.currency, countryConfig.locale),
      country,
      locale: countryConfig.locale
    }
    
    console.log('🌍 Langue changée vers:', language, 'Nouvelle config:', this.currentConfig)
  }

  /**
   * Initialise le service de localisation
   */
  public async initialize(): Promise<LocalizationConfig> {
    const config = await this.detectLocalization()

    const urlLang = new URLSearchParams(window.location.search).get('lng')
    const explicitOverride = urlLang && SUPPORTED_LANGS.includes(urlLang)

    if (!explicitOverride && i18n.language !== config.language) {
      await i18n.changeLanguage(config.language)
    }

    return config
  }

  /**
   * Obtient les données de prix pour l'API de paiement
   */
  public async getPaymentData(): Promise<{ amount: number; currency: string }> {
    const config = await this.getCurrentConfig()
    return {
      amount: config.price,
      currency: config.currency
    }
  }

  /**
   * Vérifie si la langue actuelle est le français
   */
  public async isFrench(): Promise<boolean> {
    const config = await this.getCurrentConfig()
    return config.language === 'fr'
  }

  /**
   * Vérifie si la devise actuelle est l'Euro
   */
  public async isEuro(): Promise<boolean> {
    const config = await this.getCurrentConfig()
    return config.currency === 'EUR'
  }

  /**
   * Vérifie si la devise actuelle est le Dollar australien
   */
  public async isAUD(): Promise<boolean> {
    const config = await this.getCurrentConfig()
    return config.currency === 'AUD'
  }

  /**
   * Vérifie si la devise actuelle est le Dollar néo-zélandais
   */
  public async isNZD(): Promise<boolean> {
    const config = await this.getCurrentConfig()
    return config.currency === 'NZD'
  }

  /**
   * Vérifie si la devise actuelle est le Dollar canadien
   */
  public async isCAD(): Promise<boolean> {
    const config = await this.getCurrentConfig()
    return config.currency === 'CAD'
  }

  /**
   * Vérifie si la devise actuelle est la Livre sterling
   */
  public async isGBP(): Promise<boolean> {
    const config = await this.getCurrentConfig()
    return config.currency === 'GBP'
  }

  /**
   * Mapping pur: code i18n -> locale PayZen/SystemPay (kr-language)
   */
  public mapPayZenLocale(language?: string): string {
    const map: Record<string, string> = {
      fr: 'fr-FR',
      de: 'de-DE',
      es: 'es-ES',
      it: 'it-IT',
      en: 'en-GB'
    }
    return (language && map[language]) || 'en-GB'
  }

  /**
   * Obtient le code de langue pour SystemPay (variante async, lit la config courante)
   */
  public async getSystemPayLanguage(): Promise<string> {
    const config = await this.getCurrentConfig()
    return this.mapPayZenLocale(config.language)
  }
}

// Instance singleton
export const localizationService = new LocalizationService()
export default localizationService
